import { GameList } from '@/common/components/GameList';
import { makeSimpleAutoObservable } from '@/common/utils/mobx/mobx';
import { BaseViewModel, useViewModelConstructor } from '@/common/utils/mobx/ViewModel';
import { GameInfo } from '@/types';
import { css, Interpolation, Theme } from '@emotion/react';
import {
  absolute,
  Button,
  Card,
  CardBody,
  CardHeader,
  colors,
  Dialog,
  flex1,
  FlexColumn,
  FlexRow,
  font,
  fontFamilies,
  fontSizes,
  fullSize,
  fullWidth,
  Intent,
  padding,
  relative,
  ScrollArea,
} from '@toilet-paper-games/ui';
import { observer } from 'mobx-react-lite';
import { People } from 'react-bootstrap-icons';
import { ControllerAppModel, useControllerAppModel } from '../models/ControllerAppModel';
import { SelectGameButton } from './SelectGameButton';

export interface GameIconInfo extends Pick<
  GameInfo,
  'uniqueName' | 'displayName' | 'image' | 'tags'
> {}

export interface GameListViewModelProps {
  appModel: ControllerAppModel;
}

export class GameListViewModel extends BaseViewModel<GameListViewModelProps> {
  lastSelectedGame: string | null = null;
  lastSuggestedGame: string | null = null;

  constructor(props: GameListViewModelProps) {
    super(props);

    makeSimpleAutoObservable(this, {}, { autoBind: true });
  }

  suggestGame() {
    if (this.lastSelectedGame) {
      this.lastSuggestedGame = this.lastSelectedGame;
      this.props.appModel.suggestGame(this.lastSelectedGame);
    }
  }

  setLastSelectedGame(gameUniqueName: string) {
    this.lastSelectedGame = gameUniqueName;
  }
}

const mainStyle = css([flex1, fullWidth, { overflow: 'hidden' }, relative()]);
const headerStyle: Interpolation<Theme> = [
  { lineHeight: '1em', color: colors.transparentLight },
];

export const GameSelectionMenu = observer(() => {
  const appModel = useControllerAppModel();
  const viewModel = useViewModelConstructor(GameListViewModel, { appModel });

  return (
    <div css={mainStyle}>
      <div css={[absolute(0, 0, 0, 0), padding('md')]}>
        <Dialog
          css={[absolute(0, 0, 0, 0)]}
          open={appModel.uiModel.showGameInfoDialog}
          onOpenChange={appModel.uiModel.toggleShowGameInfo}
          cardProps={{ noPadding: true }}
        >
          <Card
            noPadding
            css={{
              width: 'calc(100vw - 30px)',
              height: '600px',
              maxHeight: 'calc(100vh - 200px)',
              overflow: 'clip',
            }}
          >
            <FlexColumn css={[fullSize]}>
              <CardHeader>
                <FlexRow alignItems="center" gap={5} css={headerStyle}>
                  <People />
                  <span>
                    {appModel.previewedGame?.['min-players']}-
                    {appModel.previewedGame?.['max-players']}
                  </span>
                </FlexRow>
              </CardHeader>
              <CardBody css={[font(fontFamilies.secondary, fontSizes.md), flex1]}>
                <FlexColumn css={[fullSize]}>
                  <h1>{appModel.previewedGame?.displayName}</h1>
                  <div css={[flex1, relative(), { overflow: 'hidden' }]}>
                    <div css={[absolute(0, 0, 0, 0)]}>
                      <ScrollArea>{appModel.previewedGame?.description}</ScrollArea>
                    </div>
                  </div>

                  {appModel.user?.isHost ? (
                    <SelectGameButton />
                  ) : (
                    <FlexRow center>
                      <Button
                        intent={Intent.Warning}
                        css={[
                          font(fontFamilies.primary, fontSizes.lg),
                          { marginBottom: 15 },
                        ]}
                        buttonHeight={8}
                        shadow
                        onClick={viewModel.suggestGame}
                        disabled={
                          viewModel.lastSelectedGame === viewModel.lastSuggestedGame
                        }
                      >
                        Suggest
                      </Button>
                    </FlexRow>
                  )}
                </FlexColumn>
              </CardBody>
            </FlexColumn>
          </Card>
        </Dialog>
        <GameList
          appModel={appModel}
          plainTabBody
          tabSelectorAlignment="center"
          tabsAtBottom
          onGameSelected={viewModel.setLastSelectedGame}
        />
      </div>
    </div>
  );
});
